import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Invoice } from '@work4all/models/lib/Classes/Invoice.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EntityTableV8, IEntityTableV8 } from './entity-table/EntityTableV8';
import { useEntityDataTableV8 } from './entity-table/use-entity-data-table-v8';
import { useErpTable } from './offers-table/erp-common/use-erp-table';
import { useCloneConvertErpActions } from './offers-table/hooks/use-clone-convert-erp';
import schema from './schemata/invoice-table-schema.json';
import { useDeleteEntitiesToolbarConfig } from './use-delete-entities-toolbar-config';
import { useErpHandlersTemplateV8 } from './use-erp-handlers-template-v8';

const defaultSort = [{ field: 'date', direction: SortDirection.DESCENDING }];

const secondarySort = [
  { field: 'number', direction: SortDirection.DESCENDING },
];

export function InvoicesTable(props: IEntityTableV8) {
  const { t } = useTranslation();
  const { entity: entityType } = schema as never;

  const dataTable = useEntityDataTableV8<Invoice, never>({
    ...props,
    schema: schema as never,
    defaultSort,
    secondarySort,
    enableFooter: true,
    rowModifiers: (value) => {
      return {
        isShade1: value.ra,
        isFaded: value.paid,
      };
    },
  });
  useErpTable(Entities.invoice, dataTable.data.length === 0);

  const template = useErpHandlersTemplateV8(dataTable.table);

  const { actions } = useCloneConvertErpActions({
    entity: Entities.invoice,
    data: dataTable.selectedEntities,
  });

  const custom = useMemo(
    () => ({
      left: actions,
    }),
    [actions]
  );

  const deleteConfig = useDeleteEntitiesToolbarConfig({
    entityType,
    selectedEntities: dataTable.selectedEntities,
  });

  return (
    <EntityTableV8
      {...props}
      {...dataTable}
      template={template}
      actions={{
        custom,
        remove: {
          ...deleteConfig,
          canDeleteEntity: (id) => {
            const entity = dataTable.selectedEntities.find(
              (item) => item.id === Number(id)
            );
            const canDelete = !entity.paid && !entity.ra;

            return {
              value: canDelete,
              preventMessage: canDelete
                ? null
                : t('ALERTS.OUTGOING_INVOICE_PAYMENTS_EXIST', {
                    number: entity?.number,
                  }),
            };
          },
        },
      }}
      displayFooter
    />
  );
}
